import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchVideoGenerateList, selectProcessingItems, selectvideoGeneratedList } from "@pages/library/librarySlice";
import { fetchAiCredits } from "@store/globalSlice";
import { RootState } from "@store/store";

/**
 * 轮询生成列表：每 30 秒刷新，90 分钟后自动停止；
 * 进入 library/generated 页面或 hover Library 时通过 resetPolling 重置计时；
 * 在「已生成」页且有进行中任务时，从后台/其他 Tab 切回会重新开始轮询并立即拉取一次。
 */
export const useLibraryPolling = () => {
  const dispatch = useAppDispatch();
  const processingTasks = useAppSelector(selectProcessingItems);
  const { focusType, videoType } = useAppSelector((state: RootState) => state.libraryReducer);
  const { pathname } = useLocation();

  const POLL_INTERVAL = 30 * 1000;
  const POLL_MAX_DURATION = 90 * 60 * 1000;

  const videoGeneratedList = useAppSelector(selectvideoGeneratedList);

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const deadlineRef = useRef(0);
  const skipNextFetchRef = useRef(false);
  const prevProcessingIdsRef = useRef<Set<string>>(new Set());
  const hasProcessingTasks = processingTasks.length > 0;
  const isGeneratedPage = pathname === "/library" && focusType === "video" && videoType === "generated";

  const stopPolling = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = undefined;
    deadlineRef.current = 0;
  }, []);

  const startPolling = useCallback(() => {
    deadlineRef.current = Date.now() + POLL_MAX_DURATION;
    if (intervalRef.current) return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
    } else {
      dispatch(fetchVideoGenerateList({}));
    }
    intervalRef.current = setInterval(() => {
      if (Date.now() > deadlineRef.current) {
        stopPolling();
        return;
      }
      dispatch(fetchVideoGenerateList({}));
    }, POLL_INTERVAL);
  }, [dispatch, stopPolling]);

  /** 清掉定时器后重建轮询：重置 30 分钟截止并立即拉一次（用于切回 Tab） */
  const restartPolling = useCallback(() => {
    stopPolling();
    skipNextFetchRef.current = false;
    startPolling();
  }, [stopPolling, startPolling]);

  useEffect(() => {
    dispatch(fetchVideoGenerateList({}));
    skipNextFetchRef.current = true;
  }, [dispatch]);

  useEffect(() => {
    if (hasProcessingTasks) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [hasProcessingTasks, startPolling, stopPolling]);

  useEffect(() => {
    if (isGeneratedPage && hasProcessingTasks) {
      startPolling();
    }
  }, [isGeneratedPage, hasProcessingTasks, startPolling]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isGeneratedPage || !hasProcessingTasks) return;
      restartPolling();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isGeneratedPage, hasProcessingTasks, restartPolling]);

  // 检测「进行中/排队中 → 失败」，刷新积分余额（失败时积分会退还）
  useEffect(() => {
    const currentData = videoGeneratedList.data ?? [];
    const prevIds = prevProcessingIdsRef.current;

    if (prevIds.size > 0) {
      const hasNewFailure = currentData.some((item: any) => prevIds.has(item.id) && item.jobItem?.status === "failed");
      if (hasNewFailure) {
        dispatch(fetchAiCredits());
      }
    }

    prevProcessingIdsRef.current = new Set(
      currentData
        .filter((item: any) => ["processing", "queued"].includes(item.jobItem?.status))
        .map((item: any) => item.id),
    );
  }, [videoGeneratedList.data, dispatch]);

  return { resetPolling: startPolling, processingTasks };
};
